import { type Dispatch, type SetStateAction, useCallback } from 'react';
import {
  type FieldArrayWithId,
  type UseFieldArrayRemove,
  type UseFieldArrayUpdate,
  type UseFormSetValue,
  useFormContext,
} from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { MatchingOperator } from '../../../../../../../../modules/audiences/AudienceForm/constants.ts';
import type { AudienceForm, Group } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { ListType } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import type { EnforceMarketUnionOperatorsCallOptions } from '../../../../../../../../modules/audiences/AudienceForm/utils/applyMarketForcedUnionOperators.ts';
import type { OperatorPath } from '../../../../StructureAndLogic/components/OperatorSelector.tsx';
import { audienceLocation, type SelectedLocationSegment } from '../../../constants.ts';
import { buildGroupsPath } from '../../../utils/getGroupFromPath.ts';
import { isGroup } from '../../utils/guards.ts';
import type { CriteriaFieldName } from '../GroupList.tsx';
import { useToggleState } from './useToggleState.ts';

const setGroupExpandFlags = (groupUuid: string | undefined, group: Group, map: Map<string, boolean>) => {
  if (!groupUuid) return;

  if ((group.direct?.segments?.length ?? 0) + (group.predefined?.segments?.length ?? 0) > 0) {
    map.set(`${groupUuid}_segments`, true);
  }
  if ((group.lookalike?.segments?.length ?? 0) > 0) {
    map.set(`${groupUuid}_lookalike`, true);
  }
  if ((group.demographic_age?.segments?.length ?? 0) + (group.demographic_gender?.segments?.length ?? 0) > 0) {
    map.set(`${groupUuid}_demographics`, true);
  }
  if ((group.location?.segments?.length ?? 0) > 0) {
    map.set(`${groupUuid}_location`, true);
  }
};

const computeInitialExpandState = (
  fields: FieldArrayWithId<AudienceForm, `${CriteriaFieldName}.groups`, 'id'>[]
): Map<string, boolean> => {
  const map = new Map<string, boolean>();

  for (const field of fields) {
    if (isGroup(field)) {
      setGroupExpandFlags(field.uuid, field, map);
      continue;
    }

    const subGroups = 'groups' in field ? field.groups : undefined;

    if (Array.isArray(subGroups) && subGroups.length > 0) {
      for (const subGroup of subGroups) {
        setGroupExpandFlags(subGroup.uuid, subGroup, map);
      }
    }
  }

  return map;
};

export type UseGroupListCallbacksProps = {
  fieldName: CriteriaFieldName;
  fields: FieldArrayWithId<AudienceForm, `${CriteriaFieldName}.groups`, 'id'>[];
  update: UseFieldArrayUpdate<AudienceForm, `${CriteriaFieldName}.groups`>;
  remove: UseFieldArrayRemove;
  setValue: UseFormSetValue<AudienceForm>;
  selectedIndex: number | null;
  setSelectedIndex: Dispatch<SetStateAction<number | null>>;
  listType: string;
  onExclusionsRemoved?: () => void;
  handleSelectedLocationSegmentChange: (segment: SelectedLocationSegment, index: number | string) => void;
  enforceMarketUnionOperators?: (options?: EnforceMarketUnionOperatorsCallOptions) => void;
};

export const useGroupListCallbacks = ({
  fieldName,
  fields,
  update,
  remove,
  setValue,
  selectedIndex,
  setSelectedIndex,
  listType,
  onExclusionsRemoved,
  handleSelectedLocationSegmentChange,
  enforceMarketUnionOperators,
}: UseGroupListCallbacksProps) => {
  const runEnforce = enforceMarketUnionOperators ?? (() => undefined);
  const { isExpanded, toggle, setExpanded, copyState } = useToggleState(() => computeInitialExpandState(fields));
  const { getValues } = useFormContext<AudienceForm>();
  const groupsPath = buildGroupsPath(fieldName);

  const handleToggleOperator = useCallback(() => {
    const currentOperator = getValues(`${fieldName}.operator`);
    const newOperator =
      currentOperator === MatchingOperator.INTERSECTION ? MatchingOperator.UNION : MatchingOperator.INTERSECTION;

    setValue(`${fieldName}.operator`, newOperator);
    runEnforce({ includeSellableMultipartner: true });
  }, [fieldName, setValue, getValues, runEnforce]);

  const handleOpenModal = useCallback(
    (index: number) => {
      setSelectedIndex(index);
    },
    [setSelectedIndex]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedIndex(null);
  }, [setSelectedIndex]);

  const handleConfirmDelete = useCallback(() => {
    if (selectedIndex !== null) {
      remove(selectedIndex);
      setSelectedIndex(null);

      if (fields.length === 2) {
        setValue(`${fieldName}.operator`, MatchingOperator.UNION);
      }
    }
  }, [selectedIndex, remove, setSelectedIndex, fields, setValue, fieldName]);

  const handleRemoveSubgroup = useCallback(
    (index: number, lastSubgroup?: Group) => {
      if (lastSubgroup) {
        const selectedLocationSegment = lastSubgroup.location?.segments?.[0]?.sub_category as SelectedLocationSegment;

        update(index, {
          ...lastSubgroup,
          operator: lastSubgroup.operator || MatchingOperator.UNION,
        });
        handleSelectedLocationSegmentChange(selectedLocationSegment ?? audienceLocation.Any, index);
        return;
      }

      update(index, { uuid: uuid(), operator: MatchingOperator.UNION });
    },
    [update, handleSelectedLocationSegmentChange]
  );

  const handleSetSubgroupOperator = useCallback(
    (index: number, operator: MatchingOperator) => {
      const groupOperatorPath: OperatorPath = `${groupsPath}.${index}.operator`;
      setValue(groupOperatorPath, operator);
      runEnforce({ includeSellableMultipartner: true });
    },
    [groupsPath, setValue, runEnforce]
  );

  const createSetSubgroupOperatorHandler = useCallback(
    (index: number) => (operator: MatchingOperator) => {
      handleSetSubgroupOperator(index, operator);
    },
    [handleSetSubgroupOperator]
  );

  const handleRemoveGroup = useCallback(
    (index: number, isEmptyGroup: boolean) => {
      if (listType === ListType.EXCLUDES && fields.length === 1 && onExclusionsRemoved) {
        setValue('normalized_definition.excludes', {
          operator: MatchingOperator.INTERSECTION,
          groups: [{ uuid: uuid(), operator: MatchingOperator.INTERSECTION }],
        });

        onExclusionsRemoved();
        return;
      }

      if (isEmptyGroup) {
        remove(index);

        if (fields.length === 2) {
          setValue(`${fieldName}.operator`, MatchingOperator.UNION);
        }
        return;
      }

      handleOpenModal(index);
    },
    [listType, fields, onExclusionsRemoved, setValue, remove, handleOpenModal, fieldName]
  );

  const createSubgroupRemoveHandler = useCallback(
    (index: number) => (lastSubgroup?: Group) => {
      handleRemoveSubgroup(index, lastSubgroup);
    },
    [handleRemoveSubgroup]
  );

  const handleAddSubgroup = useCallback(
    (index: number, field: FieldArrayWithId<AudienceForm, `${CriteriaFieldName}.groups`, 'id'>) => {
      const emptyGroup = { uuid: uuid(), operator: MatchingOperator.INTERSECTION };
      const hasSubgroups = 'groups' in field && !!field.groups?.length;
      const newSubgroupUuid = uuid();

      if (field.uuid) {
        copyState(field.uuid, newSubgroupUuid);
      }

      update(index, {
        uuid: field.uuid,
        operator: hasSubgroups ? field.operator : MatchingOperator.UNION,
        groups: [{ ...field, uuid: newSubgroupUuid, operator: MatchingOperator.INTERSECTION }, emptyGroup],
      });
    },
    [update, copyState]
  );

  const createAddSubgroupHandler = useCallback(
    (index: number, field: FieldArrayWithId<AudienceForm, `${CriteriaFieldName}.groups`, 'id'>) => () => {
      handleAddSubgroup(index, field);
    },
    [handleAddSubgroup]
  );

  const createRemoveGroupHandler = useCallback(
    (index: number, isEmptyGroup: boolean) => () => {
      handleRemoveGroup(index, isEmptyGroup);
    },
    [handleRemoveGroup]
  );

  return {
    isExpanded,
    toggle,
    setExpanded,
    handleToggleOperator,
    handleOpenModal,
    handleCloseModal,
    handleConfirmDelete,
    handleRemoveSubgroup,
    handleRemoveGroup,
    createSubgroupRemoveHandler,
    handleAddSubgroup,
    createAddSubgroupHandler,
    createRemoveGroupHandler,
    createSetSubgroupOperatorHandler,
  };
};
